// src/components/FillerModal.tsx
import { X } from 'lucide-react';
import bgTexture from '@assets/images/Hero/hero-bg.png';
import underDev from '@assets/images/Untitled design 1.png';

interface FillerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  email?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  imageSrc?: string;
  gmailComposeLink?: string;
  subject?: string; // ✅ Added subject prop
  body?: string; // ✅ Added body prop
}

export default function FillerModal({
  isOpen,
  onClose,
  title = 'Ongoing Development!',
  message = 'Our Appointment System is currently under development! For the meantime, you can email us at',
  email = 'placeholder@gmail.com',
  primaryLabel = 'Send us an Email',
  secondaryLabel = 'Go Back',
  onPrimaryClick,
  onSecondaryClick,
  imageSrc = underDev,
  gmailComposeLink,
  subject = 'Inquiry from AeroComp Website',
  body = 'Hi AeroComp Team,%0D%0A%0D%0AI would like to inquire about your services.%0D%0A%0D%0ARegards,%0D%0A[Your Name]',
}: FillerModalProps) {
  if (!isOpen) return null;

  const handleGmailClick = () => {
    if (gmailComposeLink) {
      window.open(gmailComposeLink, '_blank');
      return;
    }

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
      email,
    )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.open(gmailUrl, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* === Card === */}
      <div
        data-aos="zoom-in"
        onClick={(e) => e.stopPropagation()}
        className="relative mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-[#1a1a1a] bg-[#0B0B0B] text-center shadow-2xl"
      >
        {/* Backgrounds */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${bgTexture})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black/90" />

        {/* Content */}
        <div className="relative z-10 p-10">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 transition hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Image */}
          <div className="mx-auto mb-6 flex justify-center">
            <img
              src={imageSrc}
              alt="Under Development"
              className="h-52 w-52 select-none"
            />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-extrabold text-white">{title}</h2>

          {/* Message */}
          <p className="mt-3 text-sm leading-relaxed text-gray-300">
            {message}{' '}
            <span className="font-semibold text-[#FFBD59]">{email}</span> to set
            an appointment or inquire about our services.
          </p>

          {/* Buttons */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={onSecondaryClick ?? onClose}
              className="flex w-full items-center justify-center rounded-md border border-gray-600 bg-transparent px-5 py-2.5 text-sm font-medium text-gray-300 transition hover:bg-gray-800 sm:w-1/2"
            >
              {secondaryLabel}
            </button>

            <button
              onClick={onPrimaryClick ?? handleGmailClick}
              className="flex w-full items-center justify-center rounded-md bg-[#FFBD59] px-5 py-2.5 text-sm font-semibold text-black transition hover:opacity-90 sm:w-1/2"
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
